export interface CepingScanPayload {
  sourceType: string;
  patientName: string;
  idCard: string;
  gender?: string;
  birthday?: string;
  height?: string;
  weight?: string;
  answers: string;
}

export interface CepingScanDecodeResult {
  raw: string;
  decoded: string;
  isBase64Decoded: boolean;
  payload: CepingScanPayload | null;
}

function decodeUtf8FromBinary(binary: string): string {
  try {
    const encoded = binary
      .split("")
      .map((ch) => `%${ch.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join("");
    return decodeURIComponent(encoded);
  } catch (error) {
    return binary;
  }
}

function tryDecodeBase64(input: string): string | null {
  const compact = input.replace(/\s+/g, "");
  if (!compact) return null;

  const normalized = compact.replace(/-/g, "+").replace(/_/g, "/");
  if (!/^[A-Za-z0-9+/=]+$/.test(normalized)) return null;

  const padLength = (4 - (normalized.length % 4)) % 4;
  const padded = normalized + "=".repeat(padLength);

  try {
    const binary = window.atob(padded);
    return decodeUtf8FromBinary(binary);
  } catch (error) {
    return null;
  }
}

function isValidDateParts(year: number, month: number, day: number): boolean {
  if (year < 1900 || year > 2099) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  const date = new Date(year, month - 1, day);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
  );
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function parseIdCardInfo(idCard: string): { gender?: string; birthday?: string } {
  const clean = (idCard || "").trim().toUpperCase();

  // 18位身份证：YYYYMMDD，倒数第二位奇男偶女
  if (/^\d{17}[\dX]$/.test(clean)) {
    const year = Number(clean.slice(6, 10));
    const month = Number(clean.slice(10, 12));
    const day = Number(clean.slice(12, 14));
    const genderCode = Number(clean.slice(16, 17));

    const result: { gender?: string; birthday?: string } = {};
    if (isValidDateParts(year, month, day)) {
      result.birthday = `${year}-${pad2(month)}-${pad2(day)}`;
    }
    if (!Number.isNaN(genderCode)) {
      result.gender = genderCode % 2 === 1 ? "男" : "女";
    }
    return result;
  }

  // 15位身份证：YYMMDD，最后一位奇男偶女（年份按19xx补齐）
  if (/^\d{15}$/.test(clean)) {
    const year = Number(`19${clean.slice(6, 8)}`);
    const month = Number(clean.slice(8, 10));
    const day = Number(clean.slice(10, 12));
    const genderCode = Number(clean.slice(14, 15));

    const result: { gender?: string; birthday?: string } = {};
    if (isValidDateParts(year, month, day)) {
      result.birthday = `${year}-${pad2(month)}-${pad2(day)}`;
    }
    if (!Number.isNaN(genderCode)) {
      result.gender = genderCode % 2 === 1 ? "男" : "女";
    }
    return result;
  }

  return {};
}

function parsePayload(decoded: string): CepingScanPayload | null {
  const segments = decoded.split("$");
  if (segments.length < 4) return null;

  const sourceType = (segments[0] || "").trim();
  const patientName = (segments[1] || "").trim();
  const idCard = (segments[2] || "").trim();

  const maybeHeight = (segments[3] || "").trim();
  const maybeWeight = (segments[4] || "").trim();
  const hasHeightWeight =
    segments.length >= 6 &&
    /^\d+(\.\d+)?$/.test(maybeHeight) &&
    /^\d+(\.\d+)?$/.test(maybeWeight);

  const height = hasHeightWeight ? maybeHeight : "";
  const weight = hasHeightWeight ? maybeWeight : "";
  const answers = hasHeightWeight
    ? segments.slice(5).join("$").trim()
    : segments.slice(3).join("$").trim();

  if (!sourceType || !patientName || !idCard || !answers) {
    return null;
  }

  const idCardInfo = parseIdCardInfo(idCard);

  return {
    sourceType,
    patientName,
    idCard,
    gender: idCardInfo.gender,
    birthday: idCardInfo.birthday,
    height,
    weight,
    answers,
  };
}

export function decodeCepingScan(rawInput: string): CepingScanDecodeResult {
  const raw = (rawInput || "").trim();
  const withoutTerminator = raw.endsWith("#") ? raw.slice(0, -1) : raw;

  const decodedByBase64 = tryDecodeBase64(withoutTerminator);
  const decoded = (decodedByBase64 || withoutTerminator).trim();
  const payload = parsePayload(decoded);

  return {
    raw,
    decoded,
    isBase64Decoded: Boolean(decodedByBase64),
    payload,
  };
}
